/**
 * A tela das fases fora da apuração (src/app/sitePhase.ts): a página inicial do FRONT-2 na fase `home` (e, com
 * `phase="after"`, a abertura pós-apuração de 26/10, com o resultado e os serviços), a pré-apuração
 * (pedaço `preOpen`) na fase `pre` — e no recuo da `home`, enquanto a página inicial não existir, falhar ou demorar mais que
 * `HOME_WAIT_MS`. Os pedaços só são pedidos nessas fases; a apuração (AppShell) não depende deles. Enquanto o pedaço
 * chega, nada (o fundo da página).
 *
 * A página inicial (SINAL-F2-004, ORD-F-059): `src/app/homeEntry.tsx`, do FRONT-2, exporta
 * `HomePage(props: { onReplay(night: string): void })`. Ela entra por `import.meta.glob`: sem o arquivo, o build segue e a
 * fase `home` mostra a pré-apuração; com ele, o pedaço `homeEntry` é pedido só na fase `home`.
 */
import { type ComponentChildren, type ComponentType, createContext } from 'preact';
import { useContext, useEffect, useLayoutEffect, useState } from 'preact/hooks';
import { MyCityPicker } from '../components/MyCityPicker';
import { DoorPage } from '../features/door/DoorPage';
import { LiveCta } from '../features/door/LiveCta';
import { SessionNote } from '../features/door/SessionNote';
import { ErrorBoundary } from '../features/errors/ErrorBoundary';
import { Brand } from '../features/header/Brand';
import { SimulationBanner } from '../features/header/SimulationBanner';
import { canonizeHash } from '../router/navigate';
import { useConfig, useMyCityPicker } from '../state/hooks';
import { type LazyChunk, lazyChunk, useLazyChunk } from './lazyChunk';
import { loadMapChunk } from './mapSlot';
import { PartnerBarSlot } from './partnerSlot';
import { electionDayMs } from './phaseClock';
import { requestPasswordReset, type SiteAccess, sessionEmail, signIn, signOut } from './siteGate';
import { clockReady, openApp, type SitePhase } from './sitePhase';

/**
 * D3 (§72): "Escolha o seu município" nas pré-apurações. O campo fica no JS crítico e chega às páginas por prop (elas não
 * o importam): importado pelos três pedaços sob demanda, ele virava um pedaço comum, um pedido a mais antes de a forte
 * pintar (+220 ms no celular; regra dos 100 ms, §67). A página passa a classe do invólucro dela; sem a chave ou sem a
 * geografia, nada (nem o invólucro), e só este componente renderiza de novo quando a escolha muda.
 */
export function MyCitySlot({ class: cls }: { class: string }) {
  const picker = useMyCityPicker();
  return picker ? (
    <div class={cls}>
      <MyCityPicker {...picker} />
    </div>
  ) : null;
}

/**
 * Janela da pré-carga do pedaço do mapa na pré-apuração (ORD-F-146, achado 8 da auditoria), em minutos desde 00h00 do
 * dia da eleição (Brasília): de 16h00:00 a 16h55:00. Com o aviso fixo dos últimos 5 minutos, toda aba aberta pedia o
 * pedaço às 16h55:00 em ponto (uma rajada no Hosting); com o sorteio, os pedidos se espalham pelos 55 minutos.
 */
export const MAP_PRELOAD_FROM_MIN = 16 * 60;
export const MAP_PRELOAD_TO_MIN = 16 * 60 + 55;

/**
 * O instante (ms de época, no relógio da fonte) em que uma aba pré-carrega o pedaço do mapa: uniforme entre 16h00:00 e
 * 16h55:00 de `electionDate` (Brasília), pelo `random` em [0, 1) (fora disso, preso às pontas; NaN, 16h00). Antes dele,
 * nada; quem abre a página depois dele pré-carrega na hora. Pura: a casca a chama uma vez, com `Math.random`.
 */
export function preloadAtMs(random: () => number, electionDate: string): number {
  const r = random();
  const u = r > 0 ? Math.min(1, r) : 0;
  const from = electionDayMs(electionDate) + MAP_PRELOAD_FROM_MIN * 60_000;
  return from + Math.round(u * (MAP_PRELOAD_TO_MIN - MAP_PRELOAD_FROM_MIN) * 60_000);
}

let tabPreloadAt: number | null = null;

/**
 * O instante desta aba: sorteado uma vez por carga de página, na 1ª tela que o pede, e o mesmo depois (a forte e a
 * sóbria do recuo dela, a troca de fase às 00h00).
 */
function mapPreloadAt(electionDate: string): number {
  tabPreloadAt ??= preloadAtMs(Math.random, electionDate);
  return tabPreloadAt;
}

/**
 * Na página pública com a porta (ORD-F-153): às 17h00 vem a capa, não a apuração; o pedaço do mapa não é pré-carregado
 * (custo perto de zero para quem não entrou).
 */
const skipPreload = (): void => undefined;

/** No instante sorteado: o pedaço do mapa (a apuração já está no JS crítico; AVISO-F-058). */
function preloadMap(): void {
  void loadMapChunk().catch(() => undefined);
}

export const preOpenChunk = lazyChunk(() => import('../features/preopen/PreOpenPage'));
/** A pré-apuração forte (ORD-F-066), só com `features.preopen_strong`; a sóbria é o recuo dela. */
export const preOpenStrongChunk = lazyChunk(() => import('../features/preopen/PreOpenStrong'));

/** Contrato da página inicial do FRONT-2 (SINAL-F2-004). */
export interface HomeEntryModule {
  HomePage: ComponentType<{
    onReplay(night: string): void;
    liveCta?: ComponentChildren;
    /** 26/10: `after` = a abertura pós-apuração. */
    phase?: 'home' | 'after';
    /** 26/10, fase `after`: a apuração completa (com sessão) ou a porta de login (sem sessão). */
    onOpenApp?: () => void;
    /** 26/10, fase `after`: o botão leva à porta de login (o texto diz "Entrar"). */
    appNeedsLogin?: boolean;
  }>;
}

const homeModules = import.meta.glob<HomeEntryModule>('./homeEntry.tsx');
const homeImporter = homeModules['./homeEntry.tsx'];
/** O pedaço da página inicial, ou null enquanto o arquivo do FRONT-2 não existir. */
export const homeChunk: LazyChunk<HomeEntryModule> | null = homeImporter ? lazyChunk(homeImporter) : null;
/** Pedaço vazio no lugar da página inicial ausente (nunca pedido: `wanted` falso). */
const NO_HOME = lazyChunk<HomeEntryModule>(() => Promise.reject(new Error('sem a página inicial')));

/** Sem a página inicial carregada até aqui, a pré-apuração no recuo. */
export const HOME_WAIT_MS = 4000;

/** Classe no `<html>` enquanto a tela de fase está montada: a página rola em qualquer modo (base.css). */
export const PHASE_SCREEN_CLASS = 'phase-screen';

/** Abre a noite no site do replay (o menu dos replays da página inicial). */
function openReplay(night: string): void {
  const base = import.meta.env.VITE_REPLAY_URL;
  if (base) location.assign(`${base.replace(/\/$/, '')}/?noite=${encodeURIComponent(night)}`);
}

function HomeOrFallback({
  liveCta,
  gated = false,
  after = false,
  onOpenApp,
  appNeedsLogin = false,
}: {
  liveCta?: ComponentChildren;
  gated?: boolean;
  /** 26/10: a abertura pós-apuração (fase `after`). */
  after?: boolean;
  onOpenApp?: () => void;
  appNeedsLogin?: boolean;
}) {
  const home = useLazyChunk(homeChunk ?? NO_HOME, homeChunk !== null);
  const [gaveUp, setGaveUp] = useState(homeChunk === null);
  useEffect(() => {
    if (homeChunk === null || home) return;
    const id = setTimeout(() => setGaveUp(true), HOME_WAIT_MS);
    return () => clearTimeout(id);
  }, [home]);
  if (home) {
    const { HomePage } = home;
    return (
      <ErrorBoundary name="home" fallback={after ? AfterFallback : HomeFallback}>
        <HomePage
          onReplay={openReplay}
          liveCta={liveCta}
          phase={after ? 'after' : 'home'}
          onOpenApp={onOpenApp}
          appNeedsLogin={appNeedsLogin}
        />
      </ErrorBoundary>
    );
  }
  if (!gaveUp) return null;
  return after ? <AfterFallback /> : <PreOpen phase="home" liveCta={liveCta} gated={gated} />;
}

/**
 * Sem a página inicial na fase `after` (pedaço que não chega ou erro ao montar): nunca a pré-apuração (ela conta para o dia
 * 25). Com sessão (ou sem a porta), a apuração completa; sem sessão, a capa no passado (a live gravada e o "Entrar").
 */
function AfterFallback() {
  const access = useContext(AccessContext);
  useEffect(() => {
    if (access !== 'public') openApp();
  }, [access]);
  return access === 'public' ? (
    <ErrorBoundary name="door">
      <Door variant="live" after />
    </ErrorBoundary>
  ) : null;
}

/** Falha da página inicial: a pré-apuração no recuo (sem a chamada: o recuo do recuo fica o de hoje). */
function HomeFallback() {
  return <PreOpen phase="home" />;
}

function PreOpen({
  phase,
  liveCta,
  gated = false,
}: {
  phase: 'home' | 'pre';
  liveCta?: ComponentChildren;
  gated?: boolean;
}) {
  const mod = useLazyChunk(preOpenChunk, true);
  const electionDate = useConfig().election_date;
  if (!mod) return null;
  const { PreOpenPage } = mod;
  return (
    <ErrorBoundary name="preopen">
      <PreOpenPage
        phase={phase}
        ready={clockReady.value}
        brand={<Brand />}
        preloadAt={mapPreloadAt(electionDate)}
        onNearOpen={gated ? skipPreload : preloadMap}
        cityField={MyCitySlot}
        liveCta={liveCta}
        gated={gated}
      />
    </ErrorBoundary>
  );
}

/** Falha da pré-apuração forte (pedaço que não chega ou erro ao montar): a sóbria no lugar, com o mesmo relógio. */
function StrongFallback() {
  return <PreOpen phase="pre" />;
}

function PreOpenStrongScreen({ liveCta, gated = false }: { liveCta?: ComponentChildren; gated?: boolean }) {
  const mod = useLazyChunk(preOpenStrongChunk, true);
  const electionDate = useConfig().election_date;
  if (!mod) return null;
  const { PreOpenStrong } = mod;
  return (
    <PreOpenStrong
      ready={clockReady.value}
      brand={<Brand />}
      preloadAt={mapPreloadAt(electionDate)}
      onNearOpen={gated ? skipPreload : preloadMap}
      cityField={MyCitySlot}
      liveCta={liveCta}
      gated={gated}
    />
  );
}

// ── ORD-F-153: a porta da apuração (app/siteGate.ts; DECISOES §84) ──────────────────────────────────────────────────
/** A live do YouTube (VITE_LIVE_URL; vazia = o canal) e os links da capa (vazios = o link não aparece). */
const LIVE_URL = import.meta.env.VITE_LIVE_URL ?? '';
const POLLS_URL = import.meta.env.VITE_POLLS_URL ?? '';
const VIDEOS_URL = import.meta.env.VITE_VIDEOS_URL ?? '';
const REPLAY_URL = import.meta.env.VITE_REPLAY_URL ?? '';
/** 26/10: a transmissão gravada (mapa.voto/live leva ao vídeo no YouTube; dist/live.html do pacote). */
const AFTER_LIVE_PATH = '/live';

/** O acesso desta carga, para o recuo da abertura pós-apuração (que monta fora das props). */
const AccessContext = createContext<SiteAccess>('open');

/**
 * A porta: a capa das 17h00 (`live`) ou o login aberto pelo "Ver ao vivo" (`login`). `after` (26/10): os textos da capa no
 * passado (a transmissão ficou gravada).
 */
function Door({ variant, onBack, after = false }: { variant: 'live' | 'login'; onBack?: () => void; after?: boolean }) {
  const capaOnly = useConfig().features.capa_only;
  return (
    <DoorPage
      brand={<Brand />}
      variant={variant}
      after={after}
      capaOnly={capaOnly}
      liveUrl={after ? AFTER_LIVE_PATH : LIVE_URL}
      pollsUrl={POLLS_URL}
      replayUrl={REPLAY_URL}
      videosUrl={VIDEOS_URL}
      signIn={signIn}
      requestReset={requestPasswordReset}
      onBack={onBack}
    />
  );
}

/**
 * A tela de cada fase. `access` (app/siteGate.ts): `open` = o site de hoje; `public` = sem sessão (ou o pacote só da
 * capa): a página inicial e a pré-apuração com o "Ver ao vivo" (que abre o login), e a partir das 17h00 a capa com os
 * dois botões; `session` = o site de hoje com a linha de quem entrou e o "Sair" (às 17h00 a apuração entra, pelo App).
 */
export function PhaseScreen({ phase, access = 'open' }: { phase: SitePhase; access?: SiteAccess }) {
  const cfg = useConfig();
  const strong = cfg.features.preopen_strong;
  const capaOnly = cfg.features.capa_only;
  const [login, setLogin] = useState(false);
  // sem sessão (ou no pacote só da capa), às 17h00 vem a capa da live: a pré-apuração não promete os números ali
  const gated = access === 'public';
  const back = () => {
    setLogin(false);
    window.scrollTo(0, 0);
  };
  const open = () => {
    setLogin(true);
    window.scrollTo(0, 0);
  };
  const after = phase === 'after';
  // a chamada antes das 17h00: sem sessão, "Ver ao vivo" (nada no pacote só da capa); com sessão, a linha e o "Sair". Na
  // abertura pós-apuração (26/10), nada de "ao vivo": o acesso à apuração é o "Ver a apuração completa" da página
  const liveCta =
    access === 'public' ? (
      capaOnly || after ? null : (
        <LiveCta onOpen={open} />
      )
    ) : access === 'session' ? (
      <SessionNote email={sessionEmail()} onSignOut={signOut} />
    ) : null;
  // 26/10, fase `after`: com sessão (ou sem a porta), a apuração no lugar da página; sem sessão, a porta de login (nada
  // no pacote só da capa, que não tem "Entrar"). Antes da porta, o endereço ganha a rota: depois do login a página
  // recarrega e abre a apuração, não a abertura de novo
  const onOpenApp =
    access === 'public'
      ? capaOnly
        ? undefined
        : () => {
            canonizeHash();
            open();
          }
      : () => {
          openApp();
          window.scrollTo(0, 0);
        };
  useLayoutEffect(() => {
    const root = document.documentElement;
    root.classList.add(PHASE_SCREEN_CLASS);
    return () => root.classList.remove(PHASE_SCREEN_CLASS);
  }, []);
  // A faixa "SIMULAÇÃO — dados fictícios" (§6.9, M20) mora na casca da apuração; aqui, fora dela, a tela de fase a monta
  // por conta própria: em prévia e ensaio, os nomes fictícios nunca aparecem sem o aviso (na produção, nada: não é
  // simulação). A faixa do banner (§12.5 bar; ORD-F-136: "disponível já") também: acima da página, no fluxo, na mesma caixa
  // que o esboço do boot reservou (src/boot/skeleton.ts).
  return (
    <AccessContext.Provider value={access}>
      <SimulationBanner />
      {/* fora da apuração a faixa não espera o 1º dado (na capa das 17h00, como antes, espera) */}
      <PartnerBarSlot waitForData={phase === 'live'} />
      {phase === 'live' ? (
        <ErrorBoundary name="door">
          <Door variant="live" />
        </ErrorBoundary>
      ) : login ? (
        <ErrorBoundary name="door">
          <Door variant="login" onBack={back} after={after} />
        </ErrorBoundary>
      ) : after ? (
        <HomeOrFallback
          liveCta={liveCta}
          gated={gated}
          after
          onOpenApp={onOpenApp}
          appNeedsLogin={access === 'public'}
        />
      ) : phase === 'home' ? (
        <HomeOrFallback liveCta={liveCta} gated={gated} />
      ) : strong ? (
        <ErrorBoundary name="preopen-strong" fallback={StrongFallback}>
          <PreOpenStrongScreen liveCta={liveCta} gated={gated} />
        </ErrorBoundary>
      ) : (
        <PreOpen phase="pre" liveCta={liveCta} gated={gated} />
      )}
    </AccessContext.Provider>
  );
}
